import {SelectButtonModule} from "primeng/selectbutton";

declare module "@angular/core" {
    interface ModuleWithProviders<T = any> {
        ngModule: Type<T>;
        providers?: (Provider | EnvironmentProviders)[];
    }
}
import { CustomModule } from '@custom/custom.module';
import { TokenService } from '@core/_services/token.service';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { ROUTES } from './app.routes';

import {AppRoutingModule} from './app-routing.module';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { FieldsetModule } from 'primeng/fieldset';
import { PanelModule } from 'primeng/panel';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SidebarModule } from 'primeng/sidebar';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { DataViewModule } from 'primeng/dataview';
import { TreeModule } from 'primeng/tree';
import { ToastModule } from 'primeng/toast';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DragDropModule } from 'primeng/dragdrop';

import { AppComponent } from './app.component';
import { AppLayoutModule } from './layout/app.layout.module';
import { ApiV6Service } from '@service/apiv6.service';
import { Tools } from '@service/tools';
import { RequestTools } from '@service/request.tools';
import { BasketTools } from '@service/basket.tools';
import { CustomService } from '@custom/custom.service';
import { EditorService } from '@service/editor.service';
import { DockStandardService } from '@service/dockstandard.service';
import { DynIndexService } from '@service/dynindex.service';
import { RightService } from '@service/right.service';
import { TreeService } from '@service/tree.service';
import { VariableListService } from '@service/variablelist.service';
import { ParseService } from '@service/parse.service';
import { ConnectService } from '@service/connect.service';
import { ParamUrlService } from '@service/paramurl.service';
import { CommonService } from '@service/common.service';
import { DocumentService } from '@service/document.service';
import { BasketService } from '@service/basket.service';
import { PanelService }  from '@service/panel.service';
import { AddFileService }  from '@service/addfile.service';
import { IndexedDbService } from '@service/indexeddb.service';

import { Communication } from '@service/communication';
import { DocumalisModule } from '@documalis/documalis.module';
import { SharedDocumalisModule } from '@documalis/shared-documalis.module';
import { UtilsModule } from './utils/utils.module';
import { NgxUploadModule } from '@wkoza/ngx-upload';

import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

import { MenuServiceInt } from '@core/_services/menu.serviceint';
import { MessagesService } from '@core/_services/messages.service';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { AuthService } from '@auth/auth.service';
import { TokenInterceptor } from '@auth/auth.interceptor';
import localeFr from '@angular/common/locales/fr';
import { MessageService } from 'primeng/api';

import * as PAGES from './pages';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { CheckTools } from '@service/check.tools';
import { FormatRequestTools } from '@service/formatrequest.tools';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputSwitchModule } from 'primeng/inputswitch';
import { PasswordModule } from 'primeng/password';
import { GridsterModule } from 'angular-gridster2';
import { BlockUIModule } from 'primeng/blockui';
import { AppConfigComponent } from './layout/config/app.config.component';
import {LoaderComponent} from "@core/_components/loader/loader.component";
import {RippleModule} from "primeng/ripple";
import { NotificationService } from "@service/notification.service";
import { TabViewModule } from "primeng/tabview";
import { SkeletonModule } from "primeng/skeleton";
import {PlatformModule} from "@angular/cdk/platform";

import { PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalBroadcastService, MsalModule, MsalService, MsalRedirectComponent } from '@azure/msal-angular';
import { environment } from "../environments/environment";
import { loggerCallback } from "@core/_utils/azure.utils";
import { AzureService } from "@service/azure.service";
import { WebPushService } from "@service/webpush.service";
import {CountDocsComponent} from "@documalis/countdocs";

registerLocaleData(localeFr);

@NgModule({
    imports: [
        BrowserModule,
        FormsModule,
        HttpClientModule,
        RouterModule.forRoot(ROUTES, {
            useHash: true,
            preloadingStrategy: PreloadAllModules,
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled',
            scrollOffset: [0, 64]
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        MsalModule.forRoot(new PublicClientApplication({
            // MSAL Configuration
            auth: {
                clientId: environment.msalConfig.auth.clientId,
                authority: environment.msalConfig.auth.authority,
                redirectUri: '/',
                postLogoutRedirectUri: '/'
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage
            },
            system: {
                allowNativeBroker: false, // Disables WAM Broker
                loggerOptions: {
                    loggerCallback,
                    logLevel: LogLevel.Info,
                    piiLoggingEnabled: false
                }
            }
        }), {
            // MSAL Guard Configuration
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: [...environment.apiConfig.scopes]
            },
            loginFailedRoute: '/login-failed'
        }, {
            // MSAL Interceptor Configurations
            interactionType: InteractionType.Redirect,
            protectedResourceMap: new Map([
                [environment.apiConfig.uri, environment.apiConfig.scopes]
            ])
        }),
        AppRoutingModule,
        BrowserAnimationsModule,
        DocumalisModule,
        UtilsModule,
        SharedDocumalisModule,
        DialogModule,
        InputTextareaModule,
        InputTextModule,
        FieldsetModule,
        PanelModule,
        TableModule,
        DataViewModule,
        TreeModule,
        ToastModule,
        ContextMenuModule,
        CustomModule,
        ScrollPanelModule,
        SidebarModule,
        DragDropModule,
        AutoCompleteModule,
        ToggleButtonModule,
        InputNumberModule,
        ColorPickerModule,
        InputSwitchModule,
        NgxUploadModule.forRoot(),
        GridsterModule,
        PasswordModule,
        BlockUIModule,
        AppLayoutModule,
        SelectButtonModule,
        RippleModule,
        SkeletonModule,
        PlatformModule,
        MsalModule,
        CountDocsComponent
    ],
    declarations: [
        AppComponent,
        PAGES.LoginComponent,
        PAGES.NocontentComponent,
        PAGES.NotFoundComponent,
        PAGES.DashboardComponent,
        PAGES.PageBasketListComponent,
        PAGES.PageLastConnectComponent,
        PAGES.FulltextsearchComponent,
        PAGES.PageTreeSearchComponent,
        PAGES.PageTreeApiComponent,
        PAGES.PageRequestListComponent,
        PAGES.PageRequestApiComponent,
        PAGES.ExpiredComponent,
        PAGES.PageBasketComponent,
        PAGES.PageBasketDetailComponent,
        PAGES.PreferenceComponent,
        PAGES.PageContent,
        PAGES.PageResetPwdComponent,
        PAGES.PageLogTrackComponent,
        PAGES.PageAccessRightComponent,
        PAGES.PageBasketParamComponent,
        PAGES.PageParamComponent,
        PAGES.PageBatchComponent,
        PAGES.PageMailComponent,
        PAGES.PageLockDocsComponent,
        PAGES.PageDeleteDocsComponent,
        PAGES.PageLogsComponent,
        PAGES.PageErrorComponent,
        LoaderComponent
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        MenuServiceInt,
        MessagesService,
        ApiV6Service,
        Tools,
        RequestTools,
        BasketTools,
        Communication,
        DocumalisModule,
        UtilsModule,
        SharedDocumalisModule,
        AuthService,
        TokenService,
        MessageService,
        CustomService,
        EditorService,
        DockStandardService,
        DynIndexService,
        RightService,
        TreeService,
        VariableListService,
        ParseService,
        ConnectService,
        CheckTools,
        ParamUrlService,
        CommonService,
        FormatRequestTools,
        DocumentService,
        BasketService,
        PanelService,
        NotificationService,
        AddFileService,
        IndexedDbService,
        AppConfigComponent,
        AzureService,
        WebPushService
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http);
}